import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useRef } from 'react';
import { stageModeAtom, stageRefAtom } from '@/store/boardAtoms';
import { draftStateAfterUpdate, getElementOffset, getElementPositionFromNode, getElementsMap, getNodePositionFromElement, scaleElementMask, } from '@/utils/board/stageUtils';
/**
 * Кастомный хук для обработки событий трансформации элементов на Stage
 *
 * @param params - Параметры хука
 * @returns Объект с обработчиками событий трансформации
 */
export const useStageTransformEvents = ({ boardState, setBoardState, canEdit, selectedElements, send, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Атомы состояния
    const [stageMode] = useAtom(stageModeAtom);
    const isTransformingRef = useRef(false);
    /**
     * Обработчик начала перетаскивания через Transformer
     *
     * @param e - Событие мыши
     */
    const transformerOnDragStart = useCallback((_e) => {
        isTransformingRef.current = true;
    }, []);
    /**
     * Обработчик окончания перетаскивания через Transformer
     *
     * @param e - Событие мыши
     */
    const transformerOnDragEnd = useCallback((_e) => {
        isTransformingRef.current = false;
    }, []);
    /**
     * Обработчик окончания трансформации (resize/rotate) выделенного элемента
     */
    const onTransformEnd = useCallback(() => {
        if (stageMode !== 'select')
            return;
        if (!canEdit)
            return;
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const elementsMap = getElementsMap(boardState.layers);
        const payload = [];
        selectedElements.map((el) => {
            var _a, _b, _c, _d, _f, _g, _h, _j;
            const layerLocked = (_b = (_a = elementsMap.get(el.id)) === null || _a === void 0 ? void 0 : _a.layer) === null || _b === void 0 ? void 0 : _b.locked;
            if (el.locked || layerLocked)
                return;
            const node = stage.findOne(`#node-${el.id}`);
            if (!node)
                return;
            const oldW = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
            const oldH = ((_d = el.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
            const w = Math.max(10, oldW * node.scaleX());
            const h = Math.max(10, oldH * node.scaleY());
            // Сбрасываем scale перед вычислением позиции
            node.scaleX(1);
            node.scaleY(1);
            // Вычисляем финальный offset на основе нового размера
            const { offsetX: finalOffsetX, offsetY: finalOffsetY } = getElementOffset(w, h);
            // Обновляем offset в узле (всегда, чтобы гарантировать правильное значение)
            node.offsetX(finalOffsetX);
            node.offsetY(finalOffsetY);
            // Получаем текущую позицию узла (с offset) и вычисляем реальную позицию элемента (без offset)
            const { x: currentX, y: currentY } = getElementPositionFromNode(node.x(), node.y(), finalOffsetX, finalOffsetY);
            const x = currentX;
            const y = currentY;
            const rotation = node.rotation();
            // ВАЖНО: Обновляем позицию узла в Konva СРАЗУ после вычисления, чтобы избежать визуального прыжка
            // Это гарантирует, что визуальная позиция узла совпадает с тем, что будет вычислено в BoardElement
            const nodePos = getNodePositionFromElement(x, y, finalOffsetX, finalOffsetY);
            node.x(nodePos.x);
            node.y(nodePos.y);
            // Базовые свойства элемента
            const props = Object.assign(Object.assign({}, el.props), { w, h, x, y, rotation });
            // Если размер элемента изменился и у элемента есть маска, масштабируем маску
            if ((w !== oldW || h !== oldH) && ((_f = el.props) === null || _f === void 0 ? void 0 : _f.clip)) {
                const maskUpdate = scaleElementMask(el, oldW, oldH, w, h);
                if ((_g = maskUpdate.props) === null || _g === void 0 ? void 0 : _g.clip) {
                    props.clip = maskUpdate.props.clip;
                }
            }
            // Если размер элемента изменился и у элемента есть локальная сетка, масштабируем размер сетки
            if ((w !== oldW || h !== oldH) && ((_j = (_h = el.props) === null || _h === void 0 ? void 0 : _h.localGrid) === null || _j === void 0 ? void 0 : _j.enabled)) {
                const scaleX = w / oldW;
                const scaleY = h / oldH;
                // Используем средний коэффициент масштабирования для сохранения пропорций сетки
                const averageScale = (scaleX + scaleY) / 2;
                const scaledGridSize = Math.max(10, el.props.localGrid.size * averageScale);
                props.localGrid = Object.assign(Object.assign({}, el.props.localGrid), { size: scaledGridSize });
            }
            payload.push({ id: el.id, props });
        });
        setBoardState(draftStateAfterUpdate(boardState, payload));
        send({ type: 'element.update', payload: payload });
        isTransformingRef.current = false;
    }, [stageMode, canEdit, selectedElements, boardState, store, setBoardState, send]);
    return {
        transformerOnDragStart,
        transformerOnDragEnd,
        onTransformEnd,
        isTransformingRef,
    };
};
